/**
 * Per-level bespoke content for the Primary 1–Primary 6 marketing pages.
 *
 * Each level is hand-written rather than templated so that the rendered HTML
 * exposes Singapore-specific keyword surface area (WA1/WA2/SA1/SA2, SBB,
 * Foundation vs Standard Math, AL grading, the heuristics named in the MOE
 * syllabus) and gives crawlers and AI engines distinct, citable content per
 * level instead of near-duplicate templated copy.
 *
 * Topic taxonomy (chapter lists) lives in `syllabusData.ts`.
 */

import type { ApproachPoint, ChallengeItem } from './types';

export interface PrimaryLevelContent {
  seoTitle: string;
  seoDescription: string;
  heroEyebrow: string;
  heroHeadline: string;
  heroSubhead: string;
  heroCtaLabel: string;
  challengesIntro: string;
  challenges: ChallengeItem[];
  approachTitle: string;
  approachIntro: string;
  approachPoints: ApproachPoint[];
  approachClosingLine: string;
  syllabusBody: string;
  syllabusNote: string;
  ctaSectionBody: string;
}

export type PrimaryLevelNumber = 1 | 2 | 3 | 4 | 5 | 6;

export const buildPrimaryLevelContent = (
  brandName: string
): Record<PrimaryLevelNumber, PrimaryLevelContent> => ({
  1: {
    seoTitle: `Primary 1 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 1 Math tuition in Singapore. Weekly live online classes covering number bonds, place value within 100, addition and subtraction with renaming — the MOE Primary 1 syllabus.',
    heroEyebrow: 'PRIMARY 1 MATHEMATICS',
    heroHeadline:
      'Make Primary 1 the year your child decides Math is something they can do.',
    heroSubhead:
      'Weekly live online classes for Primary 1 students in Singapore, paced for 7-year-olds, with targeted practice that builds confidence as foundations form.',
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 1 students often face:',
    challenges: [
      {
        title: 'Transitioning from kindergarten to formal Math',
        description:
          'Primary 1 is the first time most children encounter structured Math lessons with written work, school assessments, and a teacher-led pace. The shift from play-based learning to systematic computation can be disorienting for the first few months.',
      },
      {
        title: 'Internalising number bonds',
        description:
          'Number bonds — the way 7 can be split into 5 + 2, 4 + 3, or 6 + 1 — are foundational to the Singapore Math approach. Students who do not internalise them in Primary 1 will struggle with addition and subtraction fluency for the rest of primary school.',
      },
      {
        title: 'Understanding place value for the first time',
        description:
          'Grasping that the digit 2 in 23 means twenty — not two — is a real conceptual leap at age 7. Without secure place value understanding, addition and subtraction with renaming becomes a memorised trick instead of a fluent skill.',
      },
      {
        title: 'Reading and decoding simple word problems',
        description:
          'At Primary 1, reading ability and Math ability develop in parallel. Children may understand the underlying Math but struggle to decode what a word problem is asking for, especially with new vocabulary like "altogether" or "left".',
      },
      {
        title: 'Showing working in the format the school expects',
        description:
          'Primary 1 introduces the expectation of writing out steps, leaving margins, and presenting answers in the format the teacher requires. For some children, this physical discipline is a bigger hurdle than the Math itself.',
      },
      {
        title: 'Building confidence after early mistakes',
        description:
          'A few weeks of "getting it wrong" early in Primary 1 can quietly install the belief that the child is not a "Math person". Repairing this confidence damage later is much harder than preventing it now.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 1 students',
    approachIntro:
      'DeepThink supports Primary 1 students with patient, structured teaching and practice that builds confidence as well as competence:',
    approachPoints: [
      {
        title: 'Build foundations, not shortcuts',
        description:
          'Number bonds and place value are taught visually first, then practised until they become automatic — so addition and subtraction with renaming feel natural, not like a memorised trick.',
      },
      {
        title: 'Child-friendly pacing',
        description:
          'Lessons are paced for 7-year-olds. Concepts are introduced one at a time, with concrete examples before symbols, and plenty of room for questions.',
      },
      {
        title: 'Targeted practice between lessons',
        description:
          'Online practice picks up exactly where the live class left off, so children reinforce new ideas while they are still fresh — not days later when they have already faded.',
      },
      {
        title: 'Win early, win often',
        description:
          'Children get small, frequent wins through worked examples and instant feedback, building the belief that they can do Math before any "I am bad at Math" story can take root.',
      },
      {
        title: 'Parents stay in the loop',
        description:
          'After each session, parents see what was covered and what to focus on at home — without having to interpret the school worksheet themselves.',
      },
    ],
    approachClosingLine:
      'Primary 1 sets the tone for the next eleven years of school Math. Strong, confident foundations now make every later year easier.',
    syllabusBody:
      'Primary 1 is the year Singapore students move from kindergarten play-based learning into the formal MOE Primary Mathematics syllabus. The year covers numbers and number bonds within 10 then 20, place value within 100, addition and subtraction with renaming, simple multiplication and division stories, basic shapes, length in centimetres, money problems up to $1, and reading time to the half-hour. Bar models and formal multiplication tables are not yet introduced — those come in Primary 2. DeepThink covers the full syllabus in the order Singapore schools introduce topics across the year.',
    syllabusNote:
      'Singapore primary schools no longer run weighted exams in Primary 1 — MOE removed all weighted assessments (including SA1 and SA2) for P1 and P2 in 2019. Primary 1 Math is now assessed only through non-weighted bite-sized check-ins, keeping the focus on routine-building rather than exam pressure. We move at the pace each child needs to internalise these ideas properly — rushing the foundations costs far more in later years than the time saved.',
    ctaSectionBody:
      'See how DeepThink helps Primary 1 students build confident mathematical foundations through clear, supportive teaching.',
  },
  2: {
    seoTitle: `Primary 2 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 2 Math tuition in Singapore. Weekly live online classes covering multiplication tables, bar models, two-step word problems, and the MOE Primary 2 syllabus.',
    heroEyebrow: 'PRIMARY 2 MATHEMATICS',
    heroHeadline:
      'Lock in Primary 2 fluency before the Primary 3 step-up arrives.',
    heroSubhead:
      "Weekly live online classes for Primary 2 students in Singapore, paired with targeted practice that turns this year's foundations into the habits Primary 3 will demand.",
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 2 students often face:',
    challenges: [
      {
        title: 'Mastering multiplication and division for the first time',
        description:
          'Primary 2 introduces multiplication and division as proper operations, not just repeated addition. Students who have not secured addition and subtraction fluency from Primary 1 find the jump to multiplication tables noticeably harder.',
      },
      {
        title: 'Moving from concrete to pictorial bar models',
        description:
          'The CPA (concrete-pictorial-abstract) approach asks students to interpret bar models and diagrams. Some children can do the arithmetic but cannot connect a picture of a bar to the numbers it represents.',
      },
      {
        title: 'Handling two-step word problems',
        description:
          'Word problems now require two operations in sequence. Students often complete the first step correctly but lose track of what the question is ultimately asking — answering the wrong final question.',
      },
      {
        title: 'Memorising tables without losing meaning',
        description:
          'Schools push for table fluency, but rote memorisation without understanding (e.g. knowing 6 × 4 = 24 because of equal groups, not just from a chant) leaves students stuck when the problem asks for something unfamiliar.',
      },
      {
        title: 'Building confidence with measurement',
        description:
          'Reading clocks to the minute, converting between kilograms and grams, and working with money introduce real-world context that some children find harder to reason about than pure number work.',
      },
      {
        title: 'Getting comfortable with fractions',
        description:
          'Halves, quarters, and thirds are the first taste of fractions. Children who do not get a clear visual mental model now will treat fractions as a confusing alien topic when they return in force at Primary 3.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 2 students',
    approachIntro:
      'DeepThink builds Primary 2 fluency through structured teaching and the right kind of repetition:',
    approachPoints: [
      {
        title: 'Multiplication tables, taught with meaning',
        description:
          'Tables are introduced through equal groups and arrays first, then drilled to fluency — so students know both why 6 × 4 = 24 and can recall it instantly.',
      },
      {
        title: 'Bar-model basics from day one',
        description:
          'Bar models are introduced as a habit, not a special trick. Students get used to drawing a model for every word problem, which makes the more complex models in Primary 3–6 feel familiar.',
      },
      {
        title: 'Two-step word problem coaching',
        description:
          'Lessons walk through how to identify "what is the question really asking?" so students learn to plan a two-step solution before computing.',
      },
      {
        title: 'Targeted practice closes gaps fast',
        description:
          'Online practice between lessons identifies which tables, which bar-model shapes, or which two-step patterns are still shaky, and serves more practice on those — not on what the child can already do.',
      },
      {
        title: 'Parents see what to reinforce',
        description:
          'After each session, parents see what was covered and which tables or topics need a few minutes of practice at home before the next class.',
      },
    ],
    approachClosingLine:
      'Primary 2 fluency makes Primary 3 manageable. Without it, every later topic carries an extra cognitive load that compounds.',
    syllabusBody:
      'Primary 2 is where Singapore students meet multiplication and division for the first time, learn the multiplication tables for 2, 3, 4, 5 and 10, work with numbers up to 1,000, and start using bar models — the visual tool that anchors every later year of MOE Primary Mathematics. The year also introduces fractions as halves and quarters, two-step word problems, mass in kilograms and grams, money up to $100, and reading time to the minute. DeepThink covers the full Primary 2 syllabus, with bar models drilled across topics from the very first weeks.',
    syllabusNote:
      'Singapore primary schools have no weighted exams in Primary 2 — MOE removed weighted assessments for P1 and P2 in 2019, so the year is assessed only through non-weighted bite-sized check-ins. That makes it the right year to lock in fluency without exam pressure. Multiplication tables and bar-model fluency are the two non-negotiable Primary 2 skills; both need to be secure before the Primary 3 jump in difficulty.',
    ctaSectionBody:
      'See how DeepThink helps Primary 2 students build multiplication fluency and bar-model thinking through structured weekly teaching.',
  },
  3: {
    seoTitle: `Primary 3 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 3 Math tuition in Singapore. Weekly live online classes covering fractions, multiplication tables 6–9, bar models, and the MOE Primary 3 syllabus — the year termly Weighted Assessments and the year-end SA2 paper begin.',
    heroEyebrow: 'PRIMARY 3 MATHEMATICS',
    heroHeadline:
      'Make Primary 3 the year confidence and exam habits start building together.',
    heroSubhead:
      "Weekly live online classes for Primary 3 students in Singapore, paired with targeted practice that turns the year's syllabus jump into steady, manageable progress.",
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 3 students often face:',
    challenges: [
      {
        title: 'Fractions as a new kind of number',
        description:
          'Primary 3 introduces fractions properly. Understanding that ⅓ is a single number — not "one and three" — and that ½ and 2/4 represent the same quantity is a significant conceptual shift that catches many students off guard.',
      },
      {
        title: 'Bar model complexity increases',
        description:
          'Problems now require comparison models and part-whole models with more steps. Students who relied on guessing or keyword-matching in Primary 1–2 hit a clear ceiling here.',
      },
      {
        title: 'Multiplication gaps from Primary 2 surface',
        description:
          'Tables for 6, 7, 8 and 9 are layered on top of the 2, 3, 4, 5, 10 facts students should already know. Any wobbliness in the earlier tables becomes a visible bottleneck during division and word-problem work.',
      },
      {
        title: 'First experience of formal exam papers',
        description:
          'Primary 3 is the year termly Weighted Assessments (WAs) and the year-end SA2 paper begin — the first proper Math papers most students sit. (Mid-year SA1 was removed for P3 in 2021.) WAs and SA2 still demand pacing, presentation, and the ability to handle questions students cannot solve immediately.',
      },
      {
        title: 'Larger numbers stress mental computation',
        description:
          'Numbers up to 10,000 require students to be quick and accurate with place value, regrouping, and mental shortcuts. Slow mental computation makes every word problem feel harder than it is.',
      },
      {
        title: 'Faster syllabus pace',
        description:
          'Primary 3 covers more topics in less time than Primary 1 or 2. Students who need extra practice on a concept may feel rushed before it is secure, leaving small gaps that compound into Primary 4.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 3 students',
    approachIntro:
      'DeepThink supports Primary 3 students through the syllabus jump and the first proper exam year:',
    approachPoints: [
      {
        title: 'Fractions taught visually first',
        description:
          'Equivalent fractions, simplification, and same-denominator addition and subtraction are taught with bar diagrams and number lines before symbols — so students build intuition, not just rules.',
      },
      {
        title: 'Bar-model fluency expanded',
        description:
          'Comparison models, part-whole models, and multi-step models are practised across topics, so students see how the same tool solves apparently different problems.',
      },
      {
        title: 'Gap-closing for Primary 2 weak spots',
        description:
          'Targeted practice surfaces shaky tables, place-value confusion, and weak two-step word-problem skills from Primary 2, and rebuilds them before they cripple Primary 3 topics.',
      },
      {
        title: 'Exam-style practice from term 1',
        description:
          'WA- and SA2-style questions are introduced gradually so the first formal Math paper does not feel alien. Students get used to pacing, format, and the discipline of attempting every question.',
      },
      {
        title: 'Parents see what to focus on',
        description:
          'After each lesson, parents get a clear picture of which topics are secure and which need a short review at home — making home practice efficient instead of scattershot.',
      },
    ],
    approachClosingLine:
      'Primary 3 is the year fluency, fractions, and exam habits all need to develop together. We help all three happen in parallel.',
    syllabusBody:
      'Primary 3 is the year the MOE Primary Mathematics syllabus accelerates noticeably. Students learn the multiplication tables for 6, 7, 8 and 9, work with numbers up to 10,000, meet fractions as proper numbers (equivalent fractions, simplifying, comparing, addition and subtraction with the same denominator), tackle perpendicular and parallel lines, read bar graphs with scales, work with angles (acute, right, obtuse), and start converting between km/m and kg/g. DeepThink covers the full syllabus, with bar models extended to comparison and multi-step structures from the first term.',
    syllabusNote:
      'Primary 3 is also the first year most Singapore primary schools run termly Weighted Assessments (WAs) and a year-end SA2 paper — mid-year SA1 was removed for P3 in 2021, but WAs and SA2 still mark the start of proper exam habits. WA- and SA2-style timed practice is introduced gradually from the second term so the first formal Math paper does not feel alien.',
    ctaSectionBody:
      'See how DeepThink helps Primary 3 students navigate the syllabus jump and build exam-ready routines through structured weekly teaching.',
  },
  4: {
    seoTitle: `Primary 4 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 4 Math tuition in Singapore. Weekly live online classes covering decimals, mixed-number fractions, area, and the MOE Primary 4 syllabus — the last year before SBB and PSLE prep.',
    heroEyebrow: 'PRIMARY 4 MATHEMATICS',
    heroHeadline:
      'Make Primary 4 count — the last year before the Primary 5 step-up.',
    heroSubhead:
      "Weekly live online classes for Primary 4 students in Singapore, paired with targeted practice that consolidates this year's fluency and prepares the thinking habits Primary 5 will reward.",
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 4 students often face:',
    challenges: [
      {
        title: 'Multi-step word problems with mixed topics',
        description:
          'Primary 4 word problems combine fractions, whole numbers, and early units-and-parts thinking in a single question. Students must plan a solution path before computing — a skill many have not yet practised.',
      },
      {
        title: 'Fluency between decimals, fractions, and percentages',
        description:
          'Working fluidly between decimals (to 4 dp), fractions, and percentages is expected by Primary 4. Students who treat these as separate topics struggle when problems mix them.',
      },
      {
        title: 'Bar models with fractions',
        description:
          'Bar models now have to represent fractional parts of a whole, which is much harder than partitioning into equal whole-number units. Students who skipped clean bar-model practice in Primary 3 hit a wall here.',
      },
      {
        title: 'Abstract geometry and properties',
        description:
          'Geometry moves beyond naming shapes to reasoning about angles, symmetry, and properties of squares, rectangles, and triangles. Students need to connect visual intuition with mathematical rules.',
      },
      {
        title: 'Pressure of Subject-Based Banding decisions',
        description:
          'Many Singapore primary schools use Primary 4 year-end results as the main input into Standard vs Foundation Math placement for Primary 5. The stakes feel real to parents and students for the first time.',
      },
      {
        title: 'Compounded gaps from Primary 1–3 are now expensive',
        description:
          'Unresolved weaknesses in fractions, multiplication facts, or model drawing become serious obstacles in Primary 4 — and disastrous in Primary 5. Primary 4 is the last realistic window to fix them without compromising new content.',
      },
      {
        title: 'Computational accuracy under fatigue',
        description:
          'Longer SA papers test stamina as much as ability. Students who can solve any single question often lose marks on long papers because of late-paper careless errors.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 4 students',
    approachIntro:
      'DeepThink prepares Primary 4 students for both the termly Weighted Assessment and year-end SA2 cycle and the Primary 5 step-up:',
    approachPoints: [
      {
        title: 'Fraction and decimal fluency',
        description:
          'Mixed-number arithmetic, fraction-decimal conversion, and decimal multiplication and division are drilled until they are automatic — because Primary 5 percentage and rate assume this fluency.',
      },
      {
        title: 'Bar models that actually scale',
        description:
          'Students learn the bar-model patterns that solve fraction-of-whole and comparison problems cleanly, so they enter Primary 5 percentage and rate with the right visual habits.',
      },
      {
        title: 'Word-problem strategy, not just computation',
        description:
          'Lessons coach students through reading the question, sketching a model, planning the steps, then computing — instead of jumping straight to numbers.',
      },
      {
        title: 'Targeted gap-closing from Primary 1–3',
        description:
          'Online practice surfaces specific weak skills (a forgotten table, a fraction misconception, a model-drawing habit) and works on them in parallel with current Primary 4 content.',
      },
      {
        title: 'SA-style timed practice',
        description:
          'Mixed-topic timed practice that mirrors Primary 4 Weighted Assessment and SA2 paper structures builds exam stamina and the discipline to check work before submitting.',
      },
    ],
    approachClosingLine:
      'Primary 4 is the most strategic year of the lower-primary phase. Decisions made now about whether to firm up foundations directly shape Primary 5 outcomes.',
    syllabusBody:
      'Primary 4 is the last year of consolidation before the Singapore primary syllabus intensifies. Students extend numbers to 100,000, work with multiplication by 2-digit numbers and division by a 1-digit divisor, meet mixed numbers and improper fractions, learn fraction addition and subtraction with related denominators, work with decimals to 4 decimal places (multiplying and dividing by 10, 100, 1,000), find the area of rectangles and squares, work with factors and multiples, read line graphs and pie charts, identify the nets of common solids, and build the model-drawing skills that PSLE Paper 2 problems will demand. DeepThink covers the full syllabus, deliberately bridging into Primary 5 percentage and rate in the second half of the year.',
    syllabusNote:
      'Primary 4 is also the year Subject-Based Banding decisions begin to take shape, with most Singapore schools using year-end results to recommend Standard or Foundation Mathematics for Primary 5. The Primary 5 step-up rewards students who arrive with secure fractions, decimals, and bar-model habits, which makes Primary 4 the most strategic year of the lower-primary phase.',
    ctaSectionBody:
      'See how DeepThink helps Primary 4 students consolidate fractions, decimals, and bar models before the Primary 5 step-up — through structured weekly teaching.',
  },
  5: {
    seoTitle: `Primary 5 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 5 Math tuition in Singapore. Weekly live online classes covering percentage, rate, fractions, decimals, and the MOE Primary 5 Standard Math syllabus — the critical pre-PSLE year.',
    heroEyebrow: 'PRIMARY 5 MATHEMATICS',
    heroHeadline:
      'Treat Primary 5 as the foundation of a strong PSLE Math grade.',
    heroSubhead:
      'Weekly live online classes for Primary 5 Standard Math students in Singapore, paired with targeted practice that builds the problem-solving habits PSLE will test next year.',
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 5 students often face:',
    challenges: [
      {
        title: 'Fraction division is a brand-new way of thinking',
        description:
          'Dividing by a fraction is not "just another division" — the answer can come out larger than the number you started with, the whole has to be identified before any working starts, and the bar model takes a new shape. Students often reach for the familiar procedure and get stuck.',
      },
      {
        title: 'Percentage piles on top of fractions and decimals',
        description:
          'Percentage problems often combine percentage, fraction, and decimal reasoning in one question. Students who do not have all three secure end up guessing which to apply.',
      },
      {
        title: 'Rate problems require careful unit thinking',
        description:
          'Rate (e.g. 5 sweets per dollar, 3 pages per minute) demands that students track both quantity and the unit of measurement. Word problems that change the unit mid-question catch many students out.',
      },
      {
        title: 'Bar models need to evolve',
        description:
          'Primary 5 problems often need before-after bar models or models with multiple chained units. Students whose bar-model skill stopped at simple comparison shapes hit a clear ceiling.',
      },
      {
        title: 'Step-up to multi-step word problems',
        description:
          'Primary 5 SA papers contain Paper 2-style multi-step problems that test several concepts at once. Students who solved clean topical questions in Primary 4 often freeze on these mixed-topic problems.',
      },
      {
        title: 'Compounded earlier-year gaps now block progress',
        description:
          'Weak fraction or decimal fluency from Primary 3 or 4 makes Primary 5 percentage and rate noticeably harder. By Primary 5, the cost of unresolved earlier-year gaps is at its highest.',
      },
      {
        title: 'PSLE pressure is starting to feel real',
        description:
          'Parents and schools begin treating Primary 5 as "PSLE preparation" already. Students can pick up on this, which adds emotional pressure to a year that is already academically demanding.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 5 students',
    approachIntro:
      'DeepThink prepares Primary 5 Standard Math students for both the SA cycle and the PSLE year that follows:',
    approachPoints: [
      {
        title:
          'Fractions, percentage, and rate taught with shared bar-model logic',
        description:
          'Lessons make explicit how the same bar-model thinking solves fraction, percentage, and rate problems — so students build one transferable habit instead of three disconnected toolkits.',
      },
      {
        title: 'Multi-step problem coaching',
        description:
          'Students learn to read the question, identify which concepts are involved, sketch the right bar model, and plan the steps before any computation — the exact skill PSLE Paper 2 tests.',
      },
      {
        title: 'Targeted practice closes specific weaknesses',
        description:
          'Online practice between lessons identifies which sub-skills are still shaky (e.g. percentage of a percentage, rate with changing units, fraction-of-fraction) and serves more practice on those areas.',
      },
      {
        title: 'Spaced review keeps earlier topics live',
        description:
          'Topics from earlier in Primary 5 (and from Primary 4) are surfaced again on a personalised schedule — so they are still secure when they reappear inside mixed-topic problems later in the year.',
      },
      {
        title: 'Parents see the path to PSLE',
        description:
          'After each session, parents get a clear picture of where their child stands relative to PSLE-readiness, and what the highest-leverage areas to work on at home are.',
      },
    ],
    approachClosingLine:
      'Primary 5 is where the foundations of a strong PSLE Math grade are laid. Strategic, targeted support this year materially shapes the AL band Primary 6 will produce.',
    syllabusBody:
      'Primary 5 is widely treated as the most critical year of Singapore primary Mathematics. The MOE Primary 5 Standard Math syllabus introduces percentage (% of, % increase / decrease), rate problems, decimals × and ÷ as multi-step operations, area of triangle, volume of cuboid, properties of parallelograms, rhombuses, and trapeziums, and advanced fraction operations. These are the topics that will return — combined and extended — across PSLE Paper 2 long-answer questions in Primary 6, where ratio, algebra and average are built directly on top of them. DeepThink covers the full syllabus, with bar-model and heuristic patterns drilled across topics so they are second nature by Primary 6.',
    syllabusNote:
      'Primary 5 is also the first year of Subject-Based Banding under the new SBB framework, with most Singapore schools placing students into Standard or Foundation Mathematics based on their Primary 4 results. DeepThink classes follow the Standard Mathematics syllabus.',
    ctaSectionBody:
      'See how DeepThink helps Primary 5 students master fractions, percentage, and rate through structured weekly teaching that builds PSLE-ready problem-solving habits.',
  },
  6: {
    seoTitle: `Primary 6 Math Tuition Singapore | ${brandName}`,
    seoDescription:
      'Primary 6 Math tuition in Singapore. Weekly live online classes covering ratio, percentage, circles, algebra and average — the MOE Primary 6 syllabus and PSLE preparation.',
    heroEyebrow: 'PRIMARY 6 MATHEMATICS',
    heroHeadline:
      'Walk into PSLE with the syllabus complete and exam habits already in place.',
    heroSubhead:
      'Weekly live online classes for Primary 6 Standard Math students in Singapore, paired with targeted practice that builds PSLE-readiness across the year, not just the final weeks.',
    heroCtaLabel: 'Book a free trial class',
    challengesIntro: 'Primary 6 students often face:',
    challenges: [
      {
        title: 'Algebra replaces the bar with a letter',
        description:
          'Primary 6 is the first time an unknown is written as a letter rather than drawn as a unit. Students who have solved everything with bars for five years often can neither form the expression nor read one back into the situation it describes.',
      },
      {
        title: 'Advanced ratio with changing units',
        description:
          'Primary 6 ratio problems often involve units that change after a transaction (someone gives away money, water is poured between containers). Students whose ratio practice stopped at static comparisons get stuck on these before-and-after structures.',
      },
      {
        title: 'Percentage applied to real-world scenarios',
        description:
          'Discount, GST, simple interest, and percentage change problems require students to translate financial language into Math. The arithmetic is rarely the hard part — the translation is.',
      },
      {
        title: 'Primary 6 SA1 doubles as a PSLE diagnostic',
        description:
          'Schools use the mid-year SA1 to identify which students need urgent intervention. A weaker-than-expected SA1 result reshapes the rest of the year and can unsettle students at exactly the wrong time.',
      },
      {
        title: 'AL grading creates a thin margin for error',
        description:
          'Under Achievement Level grading, AL 1 = ≥ 90 marks while AL 2 = 85–89. The gap between bands is small enough that careless errors in transcription, units, or final-answer presentation can cost an entire AL band.',
      },
      {
        title: 'Heuristics need to be applied strategically, not randomly',
        description:
          'Primary 6 students who learned the names of the heuristics (model drawing, working backwards, supposition, systematic listing) often cannot match them to question types under exam pressure.',
      },
      {
        title: 'Paper 1 calculator-free fluency under time pressure',
        description:
          'PSLE Paper 1 has no calculator and a tight time budget. Students who are not fluent with mental arithmetic, fraction simplification, and unit conversion lose marks on questions they could otherwise solve.',
      },
      {
        title: 'Paper 2 long-answer questions test multiple topics together',
        description:
          'The Paper 2 4–5 mark questions deliberately combine concepts from across Primary 5 and 6. Students who only practised topical questions during the year freeze when topics are mixed.',
      },
    ],
    approachTitle: 'How DeepThink helps Primary 6 students',
    approachIntro:
      'DeepThink runs Primary 6 as both syllabus completion and PSLE preparation in parallel:',
    approachPoints: [
      {
        title: 'New Primary 6 topics taught with PSLE intent',
        description:
          'Advanced ratio, circles, algebra, and average are taught with the question patterns PSLE actually uses, so students see real exam shapes from the first lesson on each topic, not just textbook drills.',
      },
      {
        title: 'Heuristic and bar-model fluency through targeted drilling',
        description:
          'Students see hundreds of problems sorted by heuristic type and bar-model shape, so they internalise which method fits which question pattern under exam pressure.',
      },
      {
        title: 'Paper 1 and Paper 2 timed practice across the year',
        description:
          'Students get regular timed Paper 1 sections (calculator-free) and Paper 2 long-answer sets, with marking and error analysis — building exam stamina, not just topic knowledge.',
      },
      {
        title: 'Spaced review of Primary 5 topics inside Primary 6 work',
        description:
          'Percentage, fractions, decimals, and rate from Primary 5 are deliberately woven into Primary 6 practice, so they are still automatic when they appear inside mixed-topic Paper 2 questions.',
      },
      {
        title: 'Targeted gap-closing from earlier years',
        description:
          'If specific Primary 3 or 4 weaknesses (e.g. weak tables, fraction-decimal conversion) are still costing marks, online practice surfaces and rebuilds those skills in parallel with PSLE prep.',
      },
    ],
    approachClosingLine:
      'Primary 6 students leave the year with the syllabus complete, the heuristics drilled to fluency, and timed-paper exam habits already built — not picked up in the final weeks.',
    syllabusBody:
      'Primary 6 is the PSLE year. The MOE Primary 6 Standard Mathematics syllabus introduces ratio (equivalent ratios, ratio and fraction, and units that change before and after a transaction), circles (area, circumference, semicircle, quadrant), average, algebra (letters as unknowns, simplifying expressions, and solving simple equations), percentage applied to discount / GST / interest, and the multi-step word problems that combine ratio, fraction, and percentage in a single question. Each topic is taught once during Primary 6, then revised continuously as part of PSLE preparation. DeepThink covers the full syllabus with PSLE Paper 1 and Paper 2 question structures practised continuously across the year.',
    syllabusNote:
      'Most Singapore primary schools run a single SA1 in the middle of the year and then transition into PSLE prep mode, with the PSLE itself replacing SA2 at the end of the year. PSLE Math is graded on the Achievement Level (AL) scale from AL 1 (≥ 90 marks) to AL 8 (< 20 marks); the Math AL feeds directly into the total PSLE Score that determines secondary school posting. DeepThink classes follow the Standard Mathematics syllabus.',
    ctaSectionBody:
      'See how DeepThink helps Primary 6 students complete the syllabus while building PSLE-ready exam habits — through structured weekly teaching and timed paper practice.',
  },
});
